// Printing on the store's own printers through PrintNode (Settings → Printers). A receipt or label
// is made on the STORE OS server and printed by the store computer, so it works the same from an
// iPad, a phone or a computer, with no print window. The receipt is exactly as long as it is and
// is cut right after the last line. Each print shows how it's going at the bottom of the screen:
// sending, waiting for the store computer, printing, printed, or what went wrong with Retry. A
// store without PrintNode printers prints from the browser like before. Price stickers go to the
// store's sticker printer (the Rollo) the same way, and the cash drawer (plugged into the receipt
// printer) opens by itself after a cash sale.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { get, post } from './api';
import { useSession } from './session';
import { useToast } from '../ui/kit';
import { Icon } from '../ui/icons';

export type CloudKind = 'repair_receipt' | 'repair_label' | 'repair_tech_receipt' | 'repair_tech_label4' | 'sale_receipt' | 'buyback_receipt' | 'buyback_batch_receipt' | 'order_receipt' | 'unlock_receipt' | 'daily_report' | 'daily_report_full' | 'price_sticker' | 'test_receipt' | 'test_label' | 'test_sticker' | 'test_drawer';
type Target = { name: string; computer: string | null } | null;
export interface PrintingStatus {
  cloud: boolean;
  receipt: Target;
  label: Target;
  /** The price sticker printer (3 x 2 in). */
  sticker?: Target;
  /** The cash drawer opens by itself through the receipt printer. */
  open_drawer?: boolean;
  /** "Print the receipt after every sale" is on. */
  auto_print?: boolean;
}

interface ServerJob {
  id: string; kind: CloudKind; state: PrintJob['state']; message: string | null; printer: string;
  stuck: boolean; slow?: boolean; can_retry: boolean;
}

export interface PrintJob {
  /** One per button press: sent again (a double tap or a dropped connection), it's the same print. */
  key: string;
  id: string | null;
  kind: CloudKind;
  refId: string;
  title: string;
  state: 'sending' | 'sent' | 'printing' | 'printed' | 'failed';
  message: string | null;
  printer: string | null;
  /** Waiting a long time for the store computer (it's off, or PrintNode isn't running). */
  stuck: boolean;
  /** At the printer a long time (out of paper, or the printer is off). */
  slow: boolean;
  can_retry: boolean;
  started: number;
  doneAt?: number;
  /** Prints it from this device's print window instead. */
  fallback?: () => void;
  /** Sent again with the print when it never reached the server (a price sticker's words, the drawer). */
  data?: unknown;
  openDrawer?: boolean;
}

export const isLabelKind = (k: CloudKind) => k === 'repair_label' || k === 'repair_tech_label4' || k === 'test_label' || k === 'price_sticker' || k === 'test_sticker';
/** Which of the store's printers a printout goes to, as a rule. */
const targetOf = (k: CloudKind): 'receipt' | 'label' | 'sticker' => (k === 'price_sticker' || k === 'test_sticker' ? 'sticker' : k === 'repair_label' || k === 'repair_tech_label4' || k === 'test_label' ? 'label' : 'receipt');
const newKey = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
const fromServer = (v: ServerJob): Partial<PrintJob> => ({
  id: v.id, state: v.state, message: v.message, printer: v.printer, stuck: !!v.stuck, slow: !!v.slow, can_retry: !!v.can_retry,
});

export interface PrintOut {
  kind: CloudKind;
  refId: string;
  /** What it is, for the status: "T-1234 receipt". */
  title: string;
  /** Prints from this browser (used when the store has no PrintNode printer for it). */
  browser?: () => void;
  /** A test page goes to the printer picked in Settings → Printers (its name, for the status). */
  printer?: string;
  /** Which of the store's printers it goes to, when it isn't the usual one (the tech ticket on receipt paper). */
  target?: 'receipt' | 'label' | 'sticker';
  /** One per print: the same key again is the same print (a receipt printed by itself after a sale prints once). */
  requestKey?: string;
  /** What a price sticker says (it can be edited on the screen first). */
  data?: unknown;
  /** A cash sale's receipt: the drawer opens as it prints (the server checks it was a cash sale, just now). */
  openDrawer?: boolean;
}

interface CloudPrint {
  /** This store's PrintNode printers (null until known). */
  status: PrintingStatus | null;
  refreshStatus: () => Promise<PrintingStatus | null>;
  /** Prints on the store's printer through PrintNode when it has one for this; otherwise from the browser. */
  printOut: (o: PrintOut) => Promise<void>;
  /** Opens the cash drawer after a cash sale (through the receipt printer), when the store has that on. Quiet: no message. */
  openDrawer: (event: 'sale' | 'refund' | 'paid_in' | 'paid_out' | 'no_sale', refId: string) => Promise<boolean>;
  jobFor: (kind: CloudKind, refId: string) => PrintJob | undefined;
  retry: (key: string) => Promise<void>;
  dismiss: (key: string) => void;
}

const Ctx = createContext<CloudPrint | null>(null);

/**
 * The PrintNode printing of the app that's open, for code that isn't a screen of its own (the
 * price stickers, printed from several screens through printPriceStickers). Null before sign-in.
 */
let current: CloudPrint | null = null;
export function cloudPrinter(): CloudPrint | null {
  return current;
}

export function CloudPrintProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const toast = useToast();
  const loc = me ? me.register?.location_id ?? me.location?.id ?? '' : null;
  const [status, setStatus] = useState<PrintingStatus | null>(null);
  const statusFor = useRef<{ loc: string | null; at: number }>({ loc: null, at: 0 });
  // The latest status for prints started from an older screen (right after it was asked for again).
  const statusNow = useRef<PrintingStatus | null>(null);
  statusNow.current = status;
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const polling = useRef(new Set<string>());

  const refreshStatus = useCallback(async () => {
    if (loc === null) return null;
    try {
      const s = await get<PrintingStatus>('/api/printing');
      statusFor.current = { loc, at: Date.now() };
      statusNow.current = s;
      setStatus(s);
      return s;
    } catch {
      return statusFor.current.loc === loc ? status : null;
    }
  }, [loc, status]);

  // Known when someone signs in, and again for another store.
  useEffect(() => {
    if (loc === null) { setStatus(null); statusFor.current = { loc: null, at: 0 }; return; }
    if (statusFor.current.loc !== loc) { setStatus(null); refreshStatus(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc]);

  const update = useCallback((key: string, patch: Partial<PrintJob>) => {
    setJobs((js) => js.map((j) => (j.key === key ? { ...j, ...patch, ...(patch.state === 'printed' && !j.doneAt ? { doneAt: Date.now() } : {}) } : j)));
  }, []);
  const dismiss = useCallback((key: string) => setJobs((js) => js.filter((j) => j.key !== key)), []);

  const retry = useCallback(async (key: string) => {
    const j = jobsRef.current.find((x) => x.key === key);
    if (!j || j.state === 'sending') return;
    update(key, { state: 'sending', message: null, stuck: false, slow: false, can_retry: false, doneAt: undefined, started: Date.now() });
    try {
      const v = j.id
        ? await post<ServerJob>(`/api/printing/jobs/${j.id}/retry`)
        // Never reached the server's answer: the same press again, so it can't print twice.
        : await post<ServerJob>('/api/printing/jobs', { kind: j.kind, refId: j.refId, requestKey: j.key, data: j.data, openDrawer: j.openDrawer });
      update(key, fromServer(v));
    } catch (e) {
      update(key, { state: 'failed', message: e instanceof Error ? e.message : 'It didn’t print.', can_retry: true });
    }
  }, [update]);

  const printOut = useCallback(async (o: PrintOut) => {
    let printer = o.printer ?? null;
    if (!o.kind.startsWith('test_')) {
      const fresh = statusFor.current.loc === loc && Date.now() - statusFor.current.at < 60_000;
      const st = fresh ? statusNow.current : await refreshStatus();
      const which = o.target ?? targetOf(o.kind);
      const target = st?.cloud ? st[which] ?? null : null;
      if (!target) {
        if (o.browser) o.browser();
        else toast(`No ${which === 'sticker' ? 'price sticker' : which} printer is picked for this store yet (Settings → Printers).`, 'error');
        return;
      }
      printer = target.name;
    }
    const same = jobsRef.current.filter((j) => j.kind === o.kind && j.refId === o.refId);
    const live = same.find((j) => j.state === 'sending' || ((j.state === 'sent' || j.state === 'printing') && !j.stuck));
    if (live) { toast(`${o.title} is already ${live.state === 'printing' ? 'printing' : 'on its way to the printer'}.`, 'info'); return; }
    const again = same.find((j) => j.state === 'failed' || j.stuck);
    if (again && !o.data) return retry(again.key);
    const key = o.requestKey ?? newKey();
    setJobs((js) => [...js.filter((j) => !(j.kind === o.kind && j.refId === o.refId)), {
      key, id: null, kind: o.kind, refId: o.refId, title: o.title, state: 'sending', message: null, printer,
      stuck: false, slow: false, can_retry: false, started: Date.now(), fallback: o.browser, data: o.data, openDrawer: o.openDrawer,
    }]);
    try {
      const v = await post<ServerJob>('/api/printing/jobs', { kind: o.kind, refId: o.refId, requestKey: key, data: o.data, openDrawer: o.openDrawer });
      update(key, fromServer(v));
    } catch (e) {
      update(key, { state: 'failed', message: e instanceof Error ? e.message : 'It didn’t print.', can_retry: true });
    }
  }, [loc, status, refreshStatus, retry, toast, update]);

  const openDrawer = useCallback(async (event: 'sale' | 'refund' | 'paid_in' | 'paid_out' | 'no_sale', refId: string) => {
    try {
      const fresh = statusFor.current.loc === loc && Date.now() - statusFor.current.at < 60_000;
      const st = fresh ? statusNow.current : await refreshStatus();
      if (!st?.open_drawer) return false;
      const r = await post<{ opened: boolean; reason: string | null }>('/api/printing/drawer', { event, refId });
      return r.opened;
    } catch {
      // The drawer still opens with its key.
      return false;
    }
  }, [loc, status, refreshStatus]);

  // Asks how each print is going until it's printed or failed; printed ones go away after a moment.
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      if (jobsRef.current.some((j) => j.state === 'printed' && j.doneAt && now - j.doneAt > 6_000)) {
        setJobs((js) => js.filter((j) => !(j.state === 'printed' && j.doneAt && now - j.doneAt > 6_000)));
      }
      for (const j of jobsRef.current) {
        if (!j.id || (j.state !== 'sent' && j.state !== 'printing') || polling.current.has(j.key)) continue;
        if (now - j.started > 15 * 60_000) continue; // PrintNode drops it after 15 minutes anyway
        polling.current.add(j.key);
        get<ServerJob>(`/api/printing/jobs/${j.id}`)
          .then((v) => update(j.key, fromServer(v)))
          .catch(() => undefined)
          .finally(() => polling.current.delete(j.key));
      }
    }, 2_000);
    return () => clearInterval(t);
  }, [update]);

  const jobFor = useCallback((kind: CloudKind, refId: string) => jobs.find((j) => j.kind === kind && j.refId === refId), [jobs]);

  const value: CloudPrint = { status, refreshStatus, printOut, openDrawer, jobFor, retry, dismiss };
  // Signed in: price stickers printed from any screen can use it.
  current = me ? value : null;
  useEffect(() => () => { current = null; }, []);
  return (
    <Ctx.Provider value={value}>
      {children}
      {me && jobs.length ? <PrintTray jobs={jobs} retry={retry} dismiss={dismiss} /> : null}
    </Ctx.Provider>
  );
}

export function useCloudPrint(): CloudPrint {
  const c = useContext(Ctx);
  if (!c) throw new Error('useCloudPrint outside CloudPrintProvider');
  return c;
}

/** How a print is going, in a few words. */
export function jobText(j: PrintJob): string {
  const on = j.printer ?? 'the printer';
  // The drawer test: nothing prints, the drawer opens.
  if (j.kind === 'test_drawer' && (j.state === 'printing' || j.state === 'printed')) return j.state === 'printed' ? `The drawer should be open (through ${on}).` : `Opening the drawer through ${on}…`;
  switch (j.state) {
    case 'sending': return `Sending to ${on}…`;
    case 'sent': return j.stuck ? 'The store computer hasn’t picked it up. Check that it’s on and PrintNode is running.' : 'Waiting for the store computer…';
    case 'printing': return j.slow ? `Still at ${on}. Check it has paper and is turned on.` : `Printing on ${on}…`;
    case 'printed': return `Printed on ${on}`;
    default: return j.message ?? 'It didn’t print.';
  }
}

/** The prints on their way, at the bottom of the screen: Retry, or print from this device instead. */
function PrintTray({ jobs, retry, dismiss }: { jobs: PrintJob[]; retry: (key: string) => Promise<void>; dismiss: (key: string) => void }) {
  const here = async (j: PrintJob) => {
    // Still waiting at PrintNode: cancel it so it doesn't also come out when the computer is back.
    if (j.id && j.state === 'sent') await post(`/api/printing/jobs/${j.id}/cancel`).catch(() => undefined);
    dismiss(j.key);
    j.fallback?.();
  };
  return (
    <div className="print-tray" role="status" aria-live="polite" aria-label="Printing">
      {jobs.map((j) => {
        const bad = j.state === 'failed';
        const warn = j.stuck || j.slow;
        const I = isLabelKind(j.kind) ? Icon.tag : Icon.printer;
        return (
          <div key={j.key} className={`print-job ${j.state} ${warn ? 'warn' : ''}`}>
            <span className="pj-ico">{j.state === 'printed' ? <Icon.check /> : bad || warn ? <Icon.alert /> : <I />}</span>
            <span className="pj-main">
              <span className="pj-t">{j.title}</span>
              <span className="pj-s">{bad ? `Didn’t print: ${jobText(j)}` : jobText(j)}</span>
              {bad || j.stuck ? (
                <span className="pj-acts">
                  {j.can_retry || bad ? <button type="button" className="pj-btn" onClick={() => retry(j.key)}><Icon.refresh /> Retry</button> : null}
                  {j.fallback ? <button type="button" className="pj-btn ghost" onClick={() => here(j)}>Print from this device</button> : null}
                </span>
              ) : null}
            </span>
            {j.state === 'sending' || (j.state !== 'printed' && !bad && !warn) ? <span className="pj-spin" aria-hidden="true" /> : null}
            {j.state !== 'sending' ? <button type="button" className="pj-x" aria-label={`Close ${j.title}`} onClick={() => dismiss(j.key)}><Icon.close /></button> : null}
          </div>
        );
      })}
    </div>
  );
}
